/**
 * The chrome every editor tab wears.
 *
 * Visual Reference: the header and tab strip shared across
 * `design/screens/editor-*.html` — back link, editable funnel name, status
 * chip, Preview and Publish, then the seven tabs.
 *
 * It sits OUTSIDE `AppShell`'s sidebar on purpose, exactly as the export draws
 * it: the editor's own top bar replaces the app's, because a person editing a
 * funnel is inside one thing and the way out is the "← Funnels" link rather
 * than a nav column. That is the same swap `settings: true` makes for Epic 4's
 * screens, one level further in.
 *
 * Story 6.2.
 */
import { Button } from '../ui/Button'
import { Pill } from '../ui/Pill'
import { DRAWN } from './proposal-copy'
import { ALL_TABS, type Tab, type TabId } from './tabs'

/** The four the store distinguishes; the header draws three of them. */
type SaveStatus = 'clean' | 'dirty' | 'saving' | 'failed'

/** The chip, per status — the same map the funnels list uses. */
const STATUS: Record<string, { label: string; tone: 'hot' | 'neutral' | 'review' }> = {
  published: { label: 'Published', tone: 'hot' },
  draft: { label: 'Draft', tone: 'neutral' },
  unpublished: { label: 'Unpublished', tone: 'review' },
  archived: { label: 'Archived', tone: 'neutral' },
}

/**
 * What the disabled Publish says, and it says what the SERVER says.
 *
 * `services/authz`'s `roleDenied` answers a refused publish with "You need the
 * Publish permission on this workspace to do that. It's granted separately from
 * your role — ask an Admin to turn it on." A screen that greys the button out
 * and explains it differently is two answers to one question, which is the
 * thing `AuditLog.tsx` already avoids for `can_export` in as many words: "the
 * screen and the server say the same word". `tests/publish-control.test.ts`
 * holds the two together.
 */
export const PUBLISH_DENIED =
  "You need the Publish permission on this workspace to publish it. It's granted separately " +
  'from your role — ask an Admin to turn it on.'

/** The save state while nothing exists — `editor-build`'s `drafting` and `proposal`, verbatim. */
const PENDING_SAVE = DRAWN.nothingSaved

export function EditorChrome({
  name,
  status,
  save,
  tab,
  canPublish,
  publishing,
  pending = false,
  onName,
  onTab,
  onPreview,
  onPublish,
}: {
  name: string
  status: string
  save: SaveStatus
  tab: TabId
  /**
   * NOTHING EXISTS YET — Story 27.4's `drafting` and `proposal` states. The drawing's header says
   * so: the name reads "Untitled funnel" in slate and cannot be edited, the save state is
   * "Nothing saved yet", and every tab but Build is recessed text (`editor-build`, both states).
   * `← Funnels` is the one way out that works; Preview and Publish have nothing to act on.
   */
  pending?: boolean
  /**
   * THE COLUMN, never the role (PRD §2.3, RULE-005). `useMe` carries
   * `canPublish` per workspace and the route is declared
   * `ws('editor', 'can_publish')`, so this prop and the server's check read the
   * same grant. An Editor without it can still PUT the funnel — which is why
   * "can I edit" and "can I publish" cannot share one answer here either.
   */
  canPublish: boolean
  publishing: boolean
  onName: (next: string) => void
  onTab: (next: TabId) => void
  onPreview: () => void
  onPublish: () => void
}) {
  const chip = STATUS[status] ?? STATUS['draft']!

  return (
    <>
      <div className="flex min-h-mdx-topbar flex-none flex-wrap items-center gap-x-mdx-12 gap-y-mdx-10 border-b border-mdx-line bg-mdx-white px-mdx-16 py-mdx-11">
        <a
          href="/funnels"
          className="flex-none whitespace-nowrap text-mdx-13 font-mdx-semibold text-mdx-brand-deep no-underline"
        >
          ← Funnels
        </a>

        {/*
          The name is edited in place, with no label and no save button, because
          the export draws none of either. It is a real edit and goes through the
          store like every other one — `onName` calls `edit()`.
        */}
        <input
          type="text"
          aria-label="Funnel name"
          value={name}
          readOnly={pending}
          onChange={(event) => onName(event.target.value)}
          className={[
            'min-w-0 flex-1 basis-mdx-row-identity rounded-mdx-control border border-transparent bg-transparent px-mdx-10 py-mdx-6 text-mdx-17 font-mdx-semibold tracking-mdx-h3 outline-none',
            pending ? 'text-mdx-slate' : 'text-mdx-ink hover:bg-mdx-ground focus:border-mdx-line focus:bg-mdx-white',
          ].join(' ')}
        />

        <Pill tone={chip.tone} dot>
          {chip.label}
        </Pill>

        {pending ? (
          <span
            role="status"
            aria-live="polite"
            className="inline-flex flex-none items-center whitespace-nowrap text-mdx-13 text-mdx-slate"
          >
            {PENDING_SAVE}
          </span>
        ) : (
          <SaveState save={save} />
        )}

        <span className="ml-auto flex flex-none gap-mdx-10">
          {/*
            WIRED BY STORY 16.1, and drawn-and-inert since Phase 4 before that.

            The citation these two carried is gone rather than re-pointed:
            everything behind them shipped in Stories 6.5 and 6.6, and what did
            not exist was the wiring between. `tests/disabled-controls-cite-open-questions.test.ts`
            is the instrument that would have said so — it now reads a CHECKED
            story as an expired reason, so this pair cannot go quiet again
            without something going red.

            PREVIEW IS NOT A SECOND PREVIEW. `DesignTab` renders `Preview.tsx`
            with the width controls `editor-design.html` draws beside it, and
            there is no preview route anywhere in the API. So this switches to
            the tab that already has one; a chrome button that opened its own
            would be a second implementation of a working thing, and a
            standalone preview URL is a draft leaving the dashboard (RULE-015),
            which is a capability with its own auth question rather than a
            button.

            PUBLISH IS GATED ON THE COLUMN, not on the role, and it is disabled
            rather than hidden — a control that vanishes teaches nobody that the
            permission exists. `AuditLog.tsx` set this shape for `can_export`.
          */}
          <Button variant="secondary" disabled={pending} onClick={onPreview}>
            Preview
          </Button>
          {/* While nothing exists the editor passes `canPublish={false}` (Story 27.4), and the
              permission's sentence is not shown: there is nothing to publish, whoever is asking. */}
          <Button
            disabled={!canPublish}
            loading={publishing ? 'Publishing' : undefined}
            title={canPublish || pending ? undefined : PUBLISH_DENIED}
            onClick={onPublish}
          >
            Publish
          </Button>
        </span>
      </div>

      <nav
        aria-label="Editor sections"
        className="flex-none overflow-x-auto border-b border-mdx-line bg-mdx-white px-mdx-16"
      >
        <div className="flex min-w-max gap-mdx-28">
          {ALL_TABS.map((entry) => (
            <TabButton
              key={entry.id}
              tab={entry}
              active={entry.id === tab}
              recessed={pending && entry.id !== 'build' && !entry.disabled}
              onTab={onTab}
            />
          ))}
        </div>
      </nav>
    </>
  )
}

function TabButton({
  tab,
  active,
  recessed,
  onTab,
}: {
  tab: Tab
  active: boolean
  /** A tab with nothing to show until the funnel exists — Story 27.4's recessed text, no mark. */
  recessed: boolean
  onTab: (next: TabId) => void
}) {
  const base = 'whitespace-nowrap border-b-2 px-mdx-4 py-mdx-12 text-mdx-15'

  if (recessed) {
    return (
      <span
        aria-disabled="true"
        className={`${base} cursor-not-allowed border-transparent font-mdx-medium text-mdx-slate opacity-mdx-recessed`}
      >
        {tab.label}
      </span>
    )
  }

  if (tab.disabled) {
    return (
      <span
        aria-disabled="true"
        className={`${base} inline-flex cursor-not-allowed items-center gap-mdx-8 border-transparent font-mdx-medium text-mdx-slate opacity-mdx-recessed`}
      >
        {/* The export's 14px square mark, drawn rather than an icon font. Its
            3px radius is `--mdx-radius-3`, added by DECISION-072 — the
            substitution DECISION-064 left behind is gone. */}
        <span
          aria-hidden="true"
          className="inline-block size-mdx-14 flex-none rounded-mdx-3 border border-solid border-mdx-slate"
        />
        {tab.label}
        {tab.badge ? (
          <span className="rounded-mdx-pill bg-mdx-nurture-tint px-mdx-9 py-mdx-3 text-mdx-12 font-mdx-semibold text-mdx-slate">
            {tab.badge}
          </span>
        ) : null}
      </span>
    )
  }

  return (
    <button
      type="button"
      aria-current={active ? 'page' : undefined}
      onClick={() => onTab(tab.id as TabId)}
      className={[
        base,
        'cursor-pointer bg-transparent transition-colors ease-mdx-swift',
        active
          ? 'border-mdx-brand font-mdx-semibold text-mdx-ink'
          : 'border-transparent font-mdx-medium text-mdx-slate hover:text-mdx-ink',
      ].join(' ')}
    >
      {tab.label}
    </button>
  )
}

/**
 * "Saved / Saving… / Not saved", beside the status chip.
 *
 * DECISION-066. Every editor export drew a header with a back link, a name, a
 * status chip and two buttons — no Save control, no indicator, no "last saved"
 * line — because autosave saves itself. What it left is a person editing a
 * document all afternoon with no way to know their work is safe, so the pass
 * drew the indicator and `editor-build.html` now carries all three states.
 *
 * `role="status"` with `aria-live="polite"`, as the export marks it: the change
 * is announced when it happens and never interrupts, which is the right volume
 * for something that is reassurance rather than news.
 *
 * FOUR STORE STATES, THREE INDICATORS. `failed` reads as "Not saved" because
 * that is what is true — the Callout beside it carries the server's own message
 * and the "Try saving again" control (RULE-078: no failure whose resolution
 * needs a human awake). A fourth red indicator would be a second, quieter
 * report of a thing already being reported loudly.
 *
 * UNDO AND REDO STAY KEYBOARD-ONLY — DECISION-066, deliberately deferred rather
 * than overlooked. The handling exists; only the control does not.
 */
function SaveState({ save }: { save: SaveStatus }) {
  return (
    <span
      role="status"
      aria-live="polite"
      className="inline-flex flex-none items-center gap-mdx-7 whitespace-nowrap text-mdx-13 text-mdx-slate"
    >
      {save === 'clean' ? (
        <>
          {/* `leading-none` is Tailwind's own `line-height: 1`, which is what
              the export sets on the glyph — a built-in utility, not a literal,
              and there is no ratio in `design/tokens.css` that means "none". */}
          <span aria-hidden="true" className="text-mdx-12 leading-none">
            ✓
          </span>
          Saved
        </>
      ) : save === 'saving' ? (
        <>
          {/*
            A pulsing RING, not a spinner: the export draws an 8px circle with a
            1.5px edge and `mdxPulse`, the same 1.4s the skeletons breathe at, so
            the one moving thing in the header moves at the system's rate.
            `--mdx-space-1-5` is the hairline token; Tailwind v4 feeds
            `--spacing-*` to width and padding but not to border-width, and the
            only class form left is the `border-[1.5px]` the lint bans.
          */}
          <span
            aria-hidden="true"
            className="size-mdx-8 flex-none animate-mdx-pulse rounded-mdx-pill border-solid border-mdx-slate"
            style={{ borderWidth: 'var(--mdx-space-1-5)', boxSizing: 'border-box' }}
          />
          Saving…
        </>
      ) : (
        <>
          <span aria-hidden="true" className="size-mdx-8 flex-none rounded-mdx-pill bg-mdx-slate" />
          Not saved
        </>
      )}
    </span>
  )
}
